import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import PageContainer from '../layout/PageContainer';
import { GET_PURCHASEORDER_BY_ID_QUERY } from '../../queries/PurchaseOrderQueries';
import type { PurchaseOrder } from '../../models/PurchaseOrder';
import dayjs from 'dayjs';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import type { InventoryItem } from '../../models/InventoryItem';
import { toUSCurrency } from '../../utilities/CurrencyUtilities';

export default function PurchaseOrderShow() {
  const { purchaseOrderId } = useParams();
  const navigate = useNavigate();

  const [getPurchaseOrderById] = useLazyQuery(
    GET_PURCHASEORDER_BY_ID_QUERY
  );

  const [purchaseOrder, setPurchaseOrder] = useState<PurchaseOrder | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadData = useCallback(async () => {
    setError(null);

    const id = +(purchaseOrderId || '0');

    if (id > 0) {
      setIsLoading(true);

      try {
        const { data, error: getPurchaseOrderByIdError } = await getPurchaseOrderById({ variables: { id } });

        setIsLoading(false);
        setError(null);

        if (getPurchaseOrderByIdError) {
          setError(getPurchaseOrderByIdError);
        }

        if (data?.purchaseOrder) {
          setPurchaseOrder(data.purchaseOrder);
        } else {
          setError({ message: 'No purchase order found' } as Error);
        }
      } catch (showDataError) {
        setError(showDataError as Error);
      }
    }
    setIsLoading(false);
  }, [getPurchaseOrderById, purchaseOrderId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBack = useCallback(() => {
    navigate('/dashboard/purchase-orders');
  }, [navigate]);

  const renderShow = useMemo(() => {
    if (isLoading) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            m: 1,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) {
      return (
        <Box sx={{ flexGrow: 1 }}>
          <Alert severity="error">{error.message}</Alert>
        </Box>
      );
    }

    return purchaseOrder ? (
      <Box sx={{ flexGrow: 1, width: '100%' }}>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid size={{ xs: 9, sm: 3 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Vendor</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <a href={`/dashboard/vendors/${purchaseOrder.vendor?.id}`}>{purchaseOrder.vendor?.name}</a>
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 9, sm: 3 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Date Purchased</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {dayjs(purchaseOrder.dateTimePurchased).format('M/D/YYYY')}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 9, sm: 3 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Total Order Cost</Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                ${toUSCurrency(purchaseOrder.totalOrderCost)}
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 12 }}>
            <Paper sx={{ px: 2, py: 1 }}>
              <Typography variant="overline">Inventory Items</Typography>
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                <Table sx={{ minWidth: 600 }} aria-label="dense table" size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '28%' }} component="th" scope="row">Name</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '18%' }} component="th" scope="row" align="right">Lot Number</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '18%' }} component="th" scope="row" align="right">Quantity Ordered (lbs)</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '18%' }} component="th" scope="row" align="right">Total Cost/Unit ($/unit)</TableCell>
                      <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '18%' }} component="th" scope="row" align="right">Total Cost ($)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(purchaseOrder.inventoryItems || []).map((inventoryItem: InventoryItem) => (
                      <TableRow
                        key={inventoryItem.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '28%' }}>
                          <a href={`/dashboard/items/${inventoryItem.item?.id}`}>{inventoryItem.item?.name}</a>
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '18%' }} align="right">
                          <a href={`/dashboard/inventory-items/${inventoryItem.id}`}>{inventoryItem.lotNumber || 'N/A'}</a>
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '18%' }} align="right">
                          {inventoryItem.quantityOrdered.toFixed(3)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '18%' }} align="right">
                          {(inventoryItem.totalCostPerUnit || 0).toFixed(3)}
                        </TableCell>
                        <TableCell sx={{ fontSize: '85%', paddingLeft: 0, width: '18%' }} align="right">
                          {toUSCurrency(inventoryItem.totalOrderedCost)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        <Divider sx={{ my: 3 }} />
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
          >
            Back
          </Button>
        </Stack>
      </Box>
    ) : null;
  }, [isLoading, error, purchaseOrder, handleBack]);

  const pageTitle = `Purchase Order ${purchaseOrderId} (Vendor: ${purchaseOrder?.vendor?.name})`;

  return (
    <PageContainer
      title={pageTitle}
      breadcrumbs={[
        { title: 'Purchase Orders', path: '/dashboard/purchase-orders' },
        { title: pageTitle },
      ]}
    >
      <Box sx={{ display: 'flex', flex: 1, width: '100%' }}>{renderShow}</Box>
    </PageContainer>
  );
}
